import React, { useRef, useState } from "react";
import useEscapeLayer from "../../useEscape";
import { outLine } from "../situation.mjs";

/*
 * OUT: one plain line under the header (frame 1i, "Out and associated"):
 * "Vendor · computer software · since Jun 12", "No company · Link to a company",
 * "Not on the board · arrived Sep 29 · Add to the board". Everything sales is
 * absent. The vendor line names the industry when one is stored, because there is
 * no vendor field to say what they sell (reality.txt [23]), and a not-a-fit line its
 * reason in words (record/situation.mjs outLine).
 *
 * The two actions are real, and drawn as the frame's links:
 *   - Link to a company searches GET /companies?q= and PATCHes the contact's
 *     company_id; the record reloads into the company's own situation;
 *   - Add to the board PATCHes pipeline_stage 'new' (the company is already a
 *     prospect: "not on the board" is a prospect with no stage).
 * A failure is said beside the control that failed.
 *
 * Props:
 *   record   the shell's bundle: view.situation (variant, since), company, contact
 *   ops      ops.patchContact(id, body), ops.patchCompany(body), ops.call(path)
 */
export default function OutLine({ record, ops }) {
  const line = outLine(record.view.situation, { company: record.company, now: record.now });
  const [linking, setLinking] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const seq = useRef(0);
  useEscapeLayer(linking, () => setLinking(false));

  const search = async (text) => {
    setQ(text);
    setError(null);
    if (text.trim().length < 2) return setHits([]);
    const mine = ++seq.current;
    try {
      const r = await ops.call(`/companies?q=${encodeURIComponent(text.trim())}&limit=8`);
      if (mine === seq.current) setHits(r.companies || []);
    } catch (e) {
      setError(e.message);
    }
  };
  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const act = () => {
    if (!line.action) return;
    if (line.action.id === "link_company") return setLinking((v) => !v);
    return run(() => ops.patchCompany({ pipeline_stage: "new" }));
  };

  return (
    <section className="rec-card rec-card--tight rec-lead rec-lead--out" aria-label="Out of the pipeline">
      <div className="rec-card__head">
        <span className="rec-strong">{line.text}</span>
        {line.action && (
          <button type="button" className="rec-link rec-lead__link" disabled={busy} aria-expanded={line.action.id === "link_company" ? linking : undefined} onClick={act}>
            {line.action.label}
          </button>
        )}
      </div>
      {linking && (
        <div className="rec-stack">
          <input className="rec-input" autoFocus placeholder="Search companies by name" value={q} onChange={(e) => search(e.target.value)} aria-label="Company name" />
          {hits.length > 0 && (
            <div className="rec-menu">
              {hits.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="rec-menu__item"
                  disabled={busy}
                  onClick={() => run(() => ops.patchContact(record.contact.id, { company_id: c.id }))}
                >
                  <span>{c.name}</span>
                  <span className="rec-menu__hint">{c.domain || ""}</span>
                </button>
              ))}
            </div>
          )}
          {q.trim().length >= 2 && !hits.length && !error && <span className="rec-meta">No company by that name yet.</span>}
        </div>
      )}
      {error && <span className="rec-err">{error}</span>}
    </section>
  );
}
